// Origine d'un destinataire (colonne addresses.origin côté API)
export const RECIPIENT_ORIGIN = {
  EXTERNAL: 'EXTERNAL',
  MANUAL: 'MANUAL',
};

// Types de numéros proposés (mêmes libellés que ceux posés par les imports)
export const PHONE_NUMBER_TYPES = [
  'Téléphone portable client',
  'Téléphone fixe client',
  'Téléphone responsable',
  'Téléphone commercial',
];

export const COMMUNICATION_PREFERENCES = [
  { value: '', label: 'Aucune' },
  { value: 'SMS', label: 'SMS' },
  { value: 'EMAIL', label: 'Email' },
];

export const EMPTY_PHONE_NUMBER = {
  name: PHONE_NUMBER_TYPES[0],
  fullname: '',
  phone_number: '',
};

export const EMPTY_RECIPIENT = {
  name: '',
  address_key: '',
  street: '',
  address_complement: '',
  zipcode: '',
  city: '',
  country: 'FR',
  email: '',
  communication_preference: '',
  latitude: null,
  longitude: null,
  phone_numbers: [],
  comments: [],
};

const toCoordinate = (value) => {
  const coordinate = parseFloat(value);
  return Number.isFinite(coordinate) ? coordinate : null;
};

/**
 * Un destinataire importé (ou antérieur à la gestion des origines) garde son identité
 * (nom, adresse) telle que transmise par le système amont
 * @param {object|null} recipient - Destinataire en modification (null en création)
 * @returns {boolean}
 */
export const isImportedRecipient = (recipient) =>
  Boolean(recipient) && recipient.origin !== RECIPIENT_ORIGIN.MANUAL;

/**
 * État du formulaire à partir du détail d'un destinataire (GET /addresses/:id)
 * @param {object|null} recipient
 * @returns {object}
 */
export const toRecipientFormState = (recipient) => {
  if (!recipient) {
    return EMPTY_RECIPIENT;
  }

  return {
    name: recipient.name || '',
    address_key: recipient.address_key || '',
    street: recipient.street || '',
    address_complement: recipient.address_complement || '',
    zipcode: recipient.zipcode || '',
    city: recipient.city || '',
    country: recipient.country || '',
    email: recipient.email || '',
    communication_preference: recipient.communication_preference || '',
    latitude: toCoordinate(recipient.latitude),
    longitude: toCoordinate(recipient.longitude),
    phone_numbers: (recipient.phone_numbers || []).map((phoneNumber) => ({
      name: phoneNumber.name || PHONE_NUMBER_TYPES[0],
      fullname: phoneNumber.fullname || '',
      phone_number:
        phoneNumber.phone_number_formatted || phoneNumber.phone_number || '',
    })),
    comments: [...(recipient.comments || [])]
      .sort((a, b) => (a.comment_index || 0) - (b.comment_index || 0))
      .map((comment) => comment.comment || ''),
  };
};

/**
 * Corps de POST /addresses ou PUT /addresses/:id. Pour un destinataire importé, le nom et
 * l'adresse ne sont pas envoyés (non modifiables) ; la clé n'est envoyée qu'à la création.
 * @param {object} formState
 * @param {object|null} recipient - Destinataire en modification (null en création)
 * @returns {object}
 */
export const toRecipientPayload = (formState, recipient) => {
  const payload = {
    address_complement: formState.address_complement.trim(),
    email: formState.email.trim(),
    communication_preference: formState.communication_preference,
    latitude: formState.latitude,
    longitude: formState.longitude,
    phone_numbers: formState.phone_numbers
      .filter((phoneNumber) => phoneNumber.phone_number.trim())
      .map((phoneNumber) => ({
        name: phoneNumber.name,
        fullname: phoneNumber.fullname.trim(),
        phone_number: phoneNumber.phone_number.trim(),
      })),
    comments: formState.comments
      .map((comment) => comment.trim())
      .filter(Boolean),
  };

  if (!isImportedRecipient(recipient)) {
    Object.assign(payload, {
      name: formState.name.trim(),
      street: formState.street.trim(),
      zipcode: formState.zipcode.trim(),
      city: formState.city.trim(),
      country: formState.country.trim(),
    });
  }

  if (!recipient) {
    payload.address_key = formState.address_key.trim();
  }

  return payload;
};

/**
 * Vérification minimale d'un email (le contrôle complet est fait par l'API)
 * @param {string} email
 * @returns {boolean}
 */
export const isEmailFormatValid = (email) =>
  !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
